<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceTotals',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    totals: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.totals,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('totals');
    }
  );

  const act_7315092382398562_reloadComponentData_ysg210j59 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_5g23q17hv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ysg210j59,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_5g23q17hv],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_5g23q17hv,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_5g23q17hv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_5g23q17hv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #content>
        <table>
          <tbody>
            <tr>
              <td>
                <ItpText :text="gettext('Net')" spacing="p-2" uppercase>
                </ItpText>
              </td>
              <td class="text-end">
                <ItpText :text="number(totals.subtotal)" spacing="p-2">
                </ItpText>
              </td>
            </tr>
            <tr>
              <td>
                <ItpText
                  :text="gettext('VAT')"
                  spacing="p-2"
                  small
                  muted
                  uppercase
                >
                </ItpText>
              </td>
            </tr>
            <tr v-for="tax in totals.taxes">
              <td>
                <ItpText
                  :text="tax.tax_code"
                  spacing="p-2"
                  small
                  muted
                  uppercase
                >
                </ItpText>
              </td>
              <td class="text-end">
                <ItpText :text="number(tax.amount)" spacing="p-2" muted small>
                </ItpText>
              </td>
            </tr>
            <tr>
              <td :colspan="2">
                <hr />
              </td>
            </tr>
            <tr>
              <td>
                <ItpText
                  :text="gettext('Total')"
                  spacing="p-2"
                  strong
                  uppercase
                >
                </ItpText>
              </td>
              <td class="text-end">
                <ItpText :text="number(totals.total)" spacing="p-2" strong>
                </ItpText>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
